import langs from 'langs';
import { getKeyValues } from './config';
import { broadcast } from '../utils/message-interceptor';
import * as logger from '../utils/logger';
import { messages, OFF } from '../constants';
import { validateSchema } from './schema-validator';

const context = cast.framework.CastReceiverContext.getInstance();
const playerManager = context.getPlayerManager();
const textTracksManager = playerManager.getTextTracksManager();
const audioTracksManager = playerManager.getAudioTracksManager();
const log = logger.create('Audio & subtitle');
const ORG = 'org',
  QAA = 'qaa',
  ISO639_2B = '2B',
  ISO639_2T = '2T',
  ISO639_1 = 1,
  LOCALIZATION = '-';
let subsOrdered,
  audiosOrdered = [],
  subtitles = [];
let currentAudio, currentSubtitle;
/**
 * Orders an Array of items by a given Array of orderings. This method is used to order subs and audios.
 *
 * @param {Array.<{language}>} items Array of items containing a language code.
 * @param {Array.<string>} ordering Array of language orders.
 * @param {boolean} audio Audio or subtitle.
 */
export function orderByLanguage(items, audio) {
  const aux = [...items];
  const itemsOrdered = [];
  const ordering = audio ? getKeyValues().audiosOrder : getKeyValues().subsOrder;

  // Sort items by order.
  ordering.forEach((order) => {
    const foundItemIndex = aux.findIndex((item) => order.indexOf(item.language) >= 0);
    if (foundItemIndex >= 0) {
      itemsOrdered.push(aux[foundItemIndex]);
      aux.splice(foundItemIndex, 1);
    }
  });

  // Add the rest of the items.
  aux.forEach((item) => itemsOrdered.push(item));
  audio ? (audiosOrdered = [...itemsOrdered]) : (subsOrdered = [...itemsOrdered]);
}

/**
 * Creates new subtitle track.
 * @param {string} item Subtitle item to be a new text track.
 * @returns {Object} New track.
 */
export async function createTrack(item) {
  const track = textTracksManager.createTrack();
  try {
    const response = await fetch(item.url);
    const data = await response.text();
    const vtt =
      'WEBVTT FILE \r\n\r\n' +
      data
        .replace(/(\d\d:\d\d:\d\d),(\d\d\d)/g, '$1.$2')
        .replace(/\{\\([ibu])\}/g, '</$1>')
        .replace(/\{\\([ibu])1\}/g, '<$1>')
        .replace(/\{([ibu])\}/g, '<$1>')
        .replace(/\{\/([ibu])\}/g, '</$1>');

    const url = URL.createObjectURL(
      new Blob([vtt], { type: cast.framework.messages.CaptionMimeType.VTT })
    );
    track.trackContentType = cast.framework.messages.CaptionMimeType.VTT;
    track.trackContentId = url;
    track.language = `${item.language}_1`; // Make sure existing tracks don't match language
    track.isInband = false;
    track.trackId = item.id;
    return track;
  } catch (e) {
    // Do nothing
  }
}

/**
 * Adds created tracks.
 * @param {Array} tracks New text tracks.
 */
export function addTracks(tracks) {
  textTracksManager.addTracks(tracks);
}

/**
 * Enables a subtitle in the player.
 * @param {string} language Language of the new subtitle.
 */
export function enableSubtitle(language) {
  textTracksManager.setActiveByLanguage(`${language}_1`);
  sendSubtitles(language);
}

/**
 * Disables a subtitle in the player.
 */
export function disableSubtitle() {
  textTracksManager.setActiveByIds(null);
  sendSubtitles(OFF);
}

/**
 * Changes the audio stream in the player.
 * @param {string} language Language of the new audio.
 */
export function changeAudio(language) {
  const aux = language.split('/');
  const auxLanguage = aux[0];
  const code = fixOrgCode(auxLanguage);
  const tracks = audioTracksManager.getTracks();
  try {
    if (code === QAA) {
      const index = tracks.findIndex((track) => track.language === QAA || track.language === ORG);
      audioTracksManager.setActiveById(tracks[index].trackId);
    } else {
      const index = tracks.findIndex(
        (track) => toISO2code(track.language) === toISO2code(auxLanguage)
      );
      audioTracksManager.setActiveById(tracks[index].trackId);
    }
    sendAudios(code);
  } catch (e) {
    log.error('Wrong audio from sender');
    audioTracksManager.setActiveByLanguage(
      audioTracksManager.getActiveTrack()?.language || tracks[0].language
    );
    sendAudios(audioTracksManager.getActiveTrack()?.language || tracks[0].language);
  }
}

/**
 * Sends information about subtitles to the receiver.
 * @param {string} selected Language of the selected subtitle.
 */
function sendSubtitles(selected) {
  const data = {
    values: subsOrdered.map((subtitle) => {
      return { key: toISO2code(subtitle.language.split('_')[0]) };
    }),
    selected: toISO2code(selected)
  };

  validateSchema('subtitles', data);

  broadcast(messages.subtitles, data);
  if (selected === OFF) {
    currentSubtitle = OFF;
  } else {
    const selectedSubtitle = subtitles.find((subtitle) => subtitle.language === selected);
    currentSubtitle = toISO2code(selectedSubtitle.language) ?? OFF;
  }
}

/**
 * Sends information about audios to the receiver.
 * @param {string} selected Language of the selected audio.
 */
export function sendAudios(selected) {
  const aux = toISO2code(selected);
  const values = audiosOrdered.map((audio) => ({
    key: `${toISO2code(audio.language)}${getAudioSufix(audio)}`
  }));
  const auxSelected = audiosOrdered.find((audio) => toISO2code(audio.language) === aux);
  const data = {
    values,
    selected: `${aux}${getAudioSufix(auxSelected)}`
  };
  validateSchema('audios', data);
  broadcast(messages.audios, data);
  currentAudio = aux;
}

/**
 * Get current tracks.
 * @return {Object} Current audio and subtitle.
 */
export function getCurrentTracks() {
  return {
    audio: currentAudio,
    subtitle: currentSubtitle
  };
}

/**
 * Language code 'org' should be 'qaa'.
 * @param {String} code
 * @returns {String} Fixed code
 */
function fixOrgCode(code) {
  return code === ORG ? QAA : code;
}

/**
 * Sends information about subtitles to the receiver.
 */
export function sendCurrentTracks() {
  sendAudios(currentAudio);
  sendSubtitles(currentSubtitle);
}

/**
 * Gets the lang for a given ISO-639-2 format code.
 * @param {String} code
 * @returns {Object} lang or undefined if code isn't found.
 */
function getLangFromCode(code) {
  const lang =
    langs.where(ISO639_2T, code) || langs.where(ISO639_2B, code) || langs.where(ISO639_1, code);

  // OR for 'es-ES, en-US'... support.
  return lang || langs.where(ISO639_1, code.split(LOCALIZATION)[0]);
}

/**
 * Gets the audio code in ISO-639-2 format for a given code in any ISO-639 format.
 * Note: This method only supports 639-1 and 639-2 entry formats for performance purpose, since no
 * other standard is used.
 * @param {String} code
 * @returns {String} entry code in ISO639_2 or same entry format if not found.
 */
function toISO2code(code) {
  const fixedCode = fixOrgCode(code),
    lang = getLangFromCode(fixedCode);

  return lang ? lang[ISO639_2T] : fixedCode;
}

/**
 * Sets subtitles from load request.
 * @param {Array} subs
 */
export function setSubtitles(subs) {
  subtitles = subs;
}

/**
 * Gets subtitles from load request.
 * @param {Array} subs
 */
export function getSubtitles() {
  return subtitles;
}

/**
 * Gets sufix needed for an audio track.
 * @param {Object} track Audio track.
 * @returns {String} Sufix for audios.
 */
function getAudioSufix(track) {
  // https://issuetracker.google.com/u/1/issues/364825885
  // Google TV canDisplayType('audio/mp4', 'ec-3') = true but it fails getting those tracks.
  // getAudioTracksManager().getTracks() always returns 'mp4a.40.2' tracks. When the issue was
  // opened, Google TV canDisplayType('audio/mp4', 'ec-3') = false, so at least it worked as
  // expected.
  const isDolbyCapable = track.audioTrackInfo.audioCodec === 'ec-3';
  const isDolbyAtmosCapable =
    isDolbyCapable &&
    context.getDeviceCapabilities()[
      cast.framework.system.DeviceCapabilities.IS_DOLBY_ATMOS_SUPPORTED
    ];
  return isDolbyAtmosCapable && track.audioTrackInfo.spatialAudio
    ? '/atmos'
    : isDolbyCapable
    ? '/dolby'
    : '';
}
